import { IProjectionMeshBase, MeshType, Projection } from "@dreamcam/projection-mesh-common";
import { LogInternal } from "@dreamcam/web-player-common";
import { DOMTokenListLike, PlayerState, WebPlayerElement, WebPlayerElementEventMap, ErrorData, PlayerStreamType, FMP4ProtocolVersion, VideoTrackList } from "@dreamcam/web-player-main-thread";
import { IScene, SceneFactory } from "./Scenes";


//#region Helper Types

type AttributeTuple = typeof AllInOnePlayer.observedAttributes;
type AttributeName = AttributeTuple[number];
type AttributedPropertyName<T extends AllInOnePlayer, K extends string & keyof T> = Lowercase<K> extends AttributeName ? K : never; 

export interface AllInOnePlayerEventMap extends WebPlayerElementEventMap {

}

export type PlayerMode = "2d" | "3d" | "auto";

//#endregion

export class AllInOnePlayer<TEngine = any> extends HTMLElement implements HTMLVideoElement {

    //#region Static methods

    public static Register(): void {
        WebPlayerElement.Register();

        if (customElements.get(AllInOnePlayer.elementName) === undefined)
            customElements.define(AllInOnePlayer.elementName, AllInOnePlayer);
    }

    //#endregion

    //#region Consts

    public static readonly elementName = "all-in-one-player"; 
    public static readonly observedAttributes: Lowercase<string & keyof AllInOnePlayer>[] = [...(WebPlayerElement.observedAttributes as Lowercase<string & keyof AllInOnePlayer>[]), "scenecolor", "zoomsensitivity", "touchmovexsensitivity", "touchmoveysensitivity", "zoommin", "zoommax", "touchzoommode", "requirereleaseafterzoom", "mousemovexsensitivity", "mousemoveysensitivity", "rotationxlimit", "rotationylimit", "currentzoom", "zoomstep", "mode", "masksrc", "projection", "hardcodedmask"];
    
    private static readonly propertyPairs: { property: string, attribute: string; }[] = [];

    private static _template: HTMLTemplateElement | null = null;

    private static get template(): HTMLTemplateElement {
        if (this._template !== null)
            return this._template;
        this._template = document.createElement("template");
        this._template.innerHTML = `
        <style id="all_in_one_player_style">        
            :host {
                display: block;
            }
            :host([hidden]) {
                display: none;
            }
            web-player, canvas {
                width: 100%;
                height: 100%;
            }
        </style>
        <web-player id="player" hidden></web-player>
        <canvas id="canvas"></canvas>`;
        return this._template;
    }

    public readonly NETWORK_EMPTY: 0 = 0;
    public readonly NETWORK_IDLE: 1 = 1;
    public readonly NETWORK_LOADING: 2 = 2;
    public readonly NETWORK_NO_SOURCE: 3 = 3;
    public readonly HAVE_NOTHING: 0 = 0;
    public readonly HAVE_METADATA: 1 = 1;
    public readonly HAVE_CURRENT_DATA: 2 = 2;
    public readonly HAVE_FUTURE_DATA: 3 = 3;
    public readonly HAVE_ENOUGH_DATA: 4 = 4;

    //#endregion

    //#region Private Fields


    private _player: WebPlayerElement | null = null;
    private _canvas: HTMLCanvasElement | null = null;
    private _scene: IScene<TEngine> | null = null;
    private _mutationObserver: MutationObserver | null = null;
    private _zoomStep: number = 1;
    private _mode: PlayerMode = "auto" 
    private _src: string = "";
    private _maskSrc: string = "";
    private _projection: string = "";
    private _includeMask: boolean = false;
    private _hardcodedMask: boolean = false;

    private get scene(): IScene<TEngine> {
        if(this._scene === null)
            this._scene = SceneFactory.Create(this.canvas);
        return this._scene;
    }
        
    private get observedAttributes(): Lowercase<string>[]
    {
        return AllInOnePlayer.observedAttributes;
    }

    //#endregion

    //#region Public Properties

    public get player(): WebPlayerElement {
        if (this._player === null)
            this._player = this.shadowRoot!.getElementById("player") as WebPlayerElement;
        return this._player;
    }

    public get canvas(): HTMLCanvasElement {
        if (this._canvas === null)
            this._canvas = this.shadowRoot!.getElementById("canvas") as HTMLCanvasElement
        return this._canvas
    }

    public get projectionMesh(): IProjectionMeshBase {
        return this.scene.projectionMesh;
    }

    public get engine(): TEngine {
        return this.scene.engine;
    }

    /**
     * Gets the current player state.
     */
    public get state(): PlayerState{
        return this.player.state;
    }
    /**
     * Gets the current engine type. 
     */
    public get currentEngine(): string{
        return this.player.currentEngine;
    }
    /**
     * Gets the Internal id of the player instance.
     */
    public get playerId(): number{
        return this.player.playerId;
    }
    /**
     * Gets a TexImageSource that can be used for rendering the player in another renderer.
     */
    public get texture(): Extract<TexImageSource, CanvasImageSource> | null{
        return this.player.texture;
    }
    /**
     * Gets the rendering width. It may differ from `videoWidth` if the device isn't capable of rendering the video at full resolution.
     */
    public get renderingWidth(): number{
        return this.player.renderingWidth;
    }
    /**
     * Gets the rendering height. It may differ from `videoHeight` if the device isn't capable of rendering the video at full resolution.
     */
    public get renderingHeight(): number{
        return this.player.renderingHeight;
    }
    /**
     * Gets the current stream type
     * [Proposal Reference](https://github.com/video-dev/media-ui-extensions/blob/main/proposals/0010-stream-type.md)
     */
    public get streamType(): PlayerStreamType{
        return this.player.streamType;
    }
    /**
     * Gets the video tracks present in the stream.
     * [WHATWG Reference](https://html.spec.whatwg.org/multipage/media.html#media-resources-with-multiple-media-tracks)
     */
    public get videoTracks(): VideoTrackList{
        return this.player.videoTracks;
    }
    /**
     * Gets all registered engines.
     */
    public get allEngines(): string[]{
        return this.player.allEngines;
    }
    /**
     * Gets all registered engines, supported by the device.
     */
    public get supportedEngines(): string[]{
        return this.player.supportedEngines;
    }
    /**
     * Gets all enabled registered engines.
     */
    public get enabledEngines(): string[]{
        return this.player.enabledEngines;
    }
    /**
     * Gets all supported and enabled engines.
     */
    public get usedEngines(): string[]{
        return this.player.usedEngines;
    }

    public get disablePictureInPicture(): boolean {
        return this.player.disablePictureInPicture;
    }

    public set disablePictureInPicture(value: boolean) {
        if (value === this.disablePictureInPicture)
            return
        this.setAttributeValue("disablePictureInPicture", value);
        this.player.disablePictureInPicture = value;
    }

    public get height(): number {
        return this.player.height;
    }

    public set height(value: number) {
        if (value === this.height)
            return
        this.setAttributeValue("height", value);
        this.player.height = value;
    }

    public get playsInline(): boolean {
        return this.player.playsInline;
    }

    public set playsInline(value: boolean) {
        if (value === this.playsInline)
            return
        this.setAttributeValue("playsInline", value);
        this.player.playsInline = value;
    }

    public get poster(): string {
        return this.player.poster;
    }

    public set poster(value: string) {
        if (value === this.poster)
            return
        this.setAttributeValue("poster", value);
        this.player.poster = value;
    }

    public get videoHeight(): number {
        return this.player.videoHeight;
    }

    public get videoWidth(): number {
        return this.player.videoWidth;
    }

    public get width(): number {
        return this.player.width;
    }

    public set width(value: number) {
        if (value === this.width)
            return
        this.setAttributeValue("width", value);
        this.player.width = value;
    }

    public get autoplay(): boolean {
        return this.player.autoplay;
    }

    public set autoplay(value: boolean) {
        if (value === this.autoplay)
            return
        this.setAttributeValue("autoplay", value);
        this.player.autoplay = value;
    }

    public get buffered(): TimeRanges {
        return this.player.buffered;
    }

    public get controls(): boolean {
        return this.player.controls;
    }

    public set controls(value: boolean) {
        if (value === this.controls)
            return
        this.setAttributeValue("controls", value);
        this.player.controls = value;
    }

    public get crossOrigin(): "" | "anonymous" | "use-credentials" | null {
        return this.player.crossOrigin;
    }

    public set crossOrigin(value: "" | "anonymous" | "use-credentials" | null) {
        if (value === this.crossOrigin)
            return
        this.setAttributeValue("crossOrigin", value);
        this.player.crossOrigin = value;
    }

    public get currentSrc(): string {
        return this.player.currentSrc;
    }

    public get currentTime(): number {
        return this.player.currentTime;
    }

    public set currentTime(value: number) {
        if (value === this.currentTime)
            return
        this.setAttributeValue("currentTime", value);
        this.player.currentTime = value;
    }

    public get defaultMuted(): boolean {
        return this.player.defaultMuted;
    }

    public set defaultMuted(value: boolean) {
        if (value === this.defaultMuted)
            return
        this.setAttributeValue("defaultMuted", value);
        this.player.defaultMuted = value;
    }

    public get defaultPlaybackRate(): number {
        return this.player.defaultPlaybackRate;
    }

    public set defaultPlaybackRate(value: number) {
        if (value === this.defaultPlaybackRate)
            return
        this.setAttributeValue("defaultPlaybackRate", value);
        this.player.defaultPlaybackRate = value;
    }
    
    public get disableRemotePlayback(): boolean {
        return this.player.disableRemotePlayback;
    }

    public set disableRemotePlayback(value: boolean) {
        if (value === this.disableRemotePlayback)
            return
        this.setAttributeValue("disableRemotePlayback", value);
        this.player.disableRemotePlayback = value;
    }

    public get duration(): number {
        return this.player.duration;
    }

    public get ended(): boolean {
        return this.player.ended;
    }

    public get error(): MediaError | null {
        return this.player.error;
    }

    public get hardcodedMask(): boolean {
        return this._hardcodedMask;
    }

    public set hardcodedMask(value: boolean) {
        if(this.hardcodedMask === value)
            return;
        this.setAttributeValue("hardcodedMask", value);
        this._hardcodedMask = value;
        this.updateMask();
    }

    public get loop(): boolean {
        return this.player.loop;
    }

    public set loop(value: boolean) {
        if (value === this.player.loop)
            return
        this.setAttributeValue("loop", value);
        this.player.loop = value;
    }

    public get maskSrc(): string {
        return this._maskSrc
    }

    public set maskSrc(value: string) {
        if(this._maskSrc === value)
            return;
        this.setAttributeValue("maskSrc", value);
        this._maskSrc = value;
        this.updateSrc(this._src, this._maskSrc);
    }

    public get mediaKeys(): MediaKeys | null {
        return this.player.mediaKeys;
    }

    public get muted(): boolean {
        return this.player.muted;
    }

    public set muted(value: boolean) {
        if (value === this.muted)
            return
        this.setAttributeValue("muted", value);
        this.player.muted = value;
    }

    public get networkState(): number {
        return this.player.networkState;
    }

    public get paused(): boolean {
        return this.player.paused;
    }

    public get playbackRate(): number {
        return this.player.playbackRate;
    }

    public set playbackRate(value: number) {
        if (value === this.playbackRate)
            return
        this.setAttributeValue("playbackRate", value);
        this.player.playbackRate = value;
    }

    public get played(): TimeRanges {
        return this.player.played;
    }

    public get preload(): "" | "none" | "metadata" | "auto" {
        return this.player.preload;
    }

    public set preload(value: "" | "none" | "metadata" | "auto") {
        if (value === this.preload)
            return
        this.setAttributeValue("preload", value);
        this.player.preload = value;
    }

    public get preservesPitch(): boolean {
        return this.player.preservesPitch;
    }

    public set preservesPitch(value: boolean) {
        if (value === this.preservesPitch)
            return
        this.setAttributeValue("preservesPitch", value);
        this.player.preservesPitch = value;
    }

    public get projection(): string {
        return this._projection;
    }

    public set projection(value: string) {
        if(this.projection === value)
            return;
        this.setAttributeValue("projection", value);
        this._projection = value;
        this.updateProjectionMesh();
    }

    public get readyState(): number {
        return this.player.readyState;
    }

    public get remote(): RemotePlayback {
        return this.player.remote;
    }

    public get seekable(): TimeRanges {
        return this.player.seekable;
    }

    public get seeking(): boolean {
        return this.player.seeking;
    }

    public get sinkId(): string {
        return this.player.sinkId;
    }

    public get src(): string {
        return this._src;
    }

    public set src(value: string) {
        if (value === this.src)
            return
        this.setAttributeValue("src", value);
        this._src = value;
        this.updateSrc(this._src, this._maskSrc);
    }

    public get srcObject(): MediaProvider | null {
        return this.player.srcObject;
    }

    public set srcObject(value: MediaProvider | null) {
        if (value === this.srcObject)
            return
        this.player.srcObject = value;
    }

    public get textTracks(): TextTrackList {
        return this.player.textTracks;
    }

    public get volume(): number {
        return this.player.volume;
    }

    public set volume(value: number) {
        if (value === this.volume)
            return
        this.setAttributeValue("volume", value);
        this.player.volume = value;
    }

    public get lowLatency(): boolean {
        return this.player.lowLatency;
    }

    public set lowLatency(value: boolean) {
        if (value === this.lowLatency)
            return
        this.setAttributeValue("lowLatency", value);
        this.player.lowLatency = value;
    }

    public get passthroughToken(): string {
        return this.player.passthroughToken;
    }

    public set passthroughToken(value: string) {
        if (value === this.passthroughToken)
            return
        this.setAttributeValue("passthroughToken", value);
        this.player.passthroughToken = value;
    }

    public get includeMask(): boolean {
        return this._includeMask;
    }

    public set includeMask(value: boolean) {
        if (value === this.includeMask)
            return
        this.setAttributeValue("includeMask", value);
        this._includeMask = value;
        this.updateMask();
    }

    public get enginesList(): string  {
        return this.player.enginesList;
    }

    public set enginesList(value:string) {
        if (value === this.enginesList)
            return
        this.setAttributeValue("enginesList", value);
        this.player.enginesList = value;
    }

    public get connectionTimeout(): number {
        return this.player.connectionTimeout;
    }

    public set connectionTimeout(value:number) {
        if (value === this.connectionTimeout)
            return
        this.setAttributeValue("connectionTimeout", value);
        this.player.connectionTimeout = value;
    }

    public get externalRenderer(): boolean {
        return this.player.externalRenderer;
    }

    public set externalRenderer(value:boolean) {
        if (value === this.externalRenderer)
            return
        this.setAttributeValue("externalRenderer", value);
        this.player.externalRenderer = value;
    }

    public get stallWindow(): number {
        return this.player.stallWindow;
    }

    public set stallWindow(value: number) {
        if (value === this.stallWindow)
            return
        this.setAttributeValue("stallWindow", value);
        this.player.stallWindow = value;
    }

    public get maxStallEvents(): number {
        return this.player.maxStallEvents;
    }

    public set maxStallEvents(value: number) {
        if (value === this.maxStallEvents)
            return
        this.setAttributeValue("maxStallEvents", value);
        this.player.maxStallEvents = value;
    }

    public get fmp4ProtocolVersion(): FMP4ProtocolVersion {
        return this.player.fmp4ProtocolVersion;
    }

    public set fmp4ProtocolVersion(value:FMP4ProtocolVersion) {
        if (value === this.fmp4ProtocolVersion)
            return
        this.setAttributeValue("fmp4ProtocolVersion", value);
        this.player.fmp4ProtocolVersion = value;
    }

    public get sceneColor(): string {
        return this.scene.sceneColor;
    }

    public set sceneColor(value: string) {
        if(this.sceneColor === value)
            return;
        this.setAttributeValue("sceneColor", value);
        this.scene.sceneColor = value;
    }

    public get zoomSensitivity(): number {
        return this.scene.zoomSensitivity;
    }

    public set zoomSensitivity(value: number) {
        if(this.zoomSensitivity === value)
            return;
        this.setAttributeValue("zoomSensitivity", value);
        this.scene.zoomSensitivity = value;
    }

    public get touchMoveXSensitivity(): number {
        return this.scene.touchMoveXSensitivity;
    }

    public set touchMoveXSensitivity(value: number) {
        if(this.touchMoveXSensitivity === value)
            return;
        this.setAttributeValue("touchMoveXSensitivity", value);
        this.scene.touchMoveXSensitivity = value;
    }

    public get touchMoveYSensitivity(): number {
        return this.scene.touchMoveYSensitivity;
    }

    public set touchMoveYSensitivity(value: number) {
        if(this.touchMoveYSensitivity === value)
            return;
        this.setAttributeValue("touchMoveYSensitivity", value);
        this.scene.touchMoveYSensitivity = value;
    }

    public get zoomMin(): number {
        return this.scene.zoomMin;
    }

    public set zoomMin(value: number) {
        if(this.zoomMin === value)
            return;
        this.setAttributeValue("zoomMin", value);
        this.scene.zoomMin = value;
    }

    public get zoomMax(): number {
        return this.scene.zoomMax;
    }

    public set zoomMax(value: number) {
        if(this.zoomMax === value)
            return;
        this.setAttributeValue("zoomMax", value);
        this.scene.zoomMax = value;
    }

    public get touchZoomMode(): 'pinch' | 'scroll' {
        return this.scene.touchZoomMode;
    }

    public set touchZoomMode(value: 'pinch' | 'scroll') {
        if(this.touchZoomMode === value)
            return;
        this.setAttributeValue("touchZoomMode", value);
        this.scene.touchZoomMode = value;
    }

    public get requireReleaseAfterZoom(): boolean {
        return this.scene.requireReleaseAfterZoom;
    }

    public set requireReleaseAfterZoom(value: boolean) {
        if(this.requireReleaseAfterZoom === value)
            return;
        this.setAttributeValue("requireReleaseAfterZoom", value);
        this.scene.requireReleaseAfterZoom = value;
    }

    public get mouseMoveXSensitivity(): number {
        return this.scene.mouseMoveXSensitivity;
    }

    public set mouseMoveXSensitivity(value: number) {
        if(this.mouseMoveXSensitivity === value)
            return;
        this.setAttributeValue("mouseMoveXSensitivity", value);
        this.scene.mouseMoveXSensitivity = value;
    }

    public get mouseMoveYSensitivity(): number {
        return this.scene.mouseMoveYSensitivity;
    }

    public set mouseMoveYSensitivity(value: number) {
        if(this.mouseMoveYSensitivity === value)
            return;
        this.setAttributeValue("mouseMoveYSensitivity", value);
        this.scene.mouseMoveYSensitivity = value;
    }

    public get rotationXLimit(): number {
        return this.scene.rotationXLimit;
    }

    public set rotationXLimit(value: number) {
        if(this.rotationXLimit === value)
            return;
        this.setAttributeValue("rotationXLimit", value);
        this.scene.rotationXLimit = value;
    }

    public get rotationYLimit(): number {
        return this.scene.rotationYLimit;
    }

    public set rotationYLimit(value: number) {
        if(this.rotationYLimit === value)
            return;
        this.setAttributeValue("rotationYLimit", value);
        this.scene.rotationYLimit = value;
    }

    public get currentZoom(): number {
        return this.scene.currentZoom;
    }

    public set currentZoom(value: number) {
        if(this.currentZoom === value)
            return;
        this.setAttributeValue("currentZoom", value);
        this.scene.currentZoom = value;
    }

    public get zoomStep(): number {
        return this._zoomStep;
    }

    public set zoomStep(value: number) {
        if(this.zoomStep === value)
            return;
        this.setAttributeValue("zoomStep", value);
        this._zoomStep = value;
    }

    public get mode(): PlayerMode {
        return this._mode;
    }

    public set mode(value: PlayerMode) {
        if(!value)
            value = "auto";
        if(this.mode === value)
            return;
        this.setAttributeValue("mode", value);
        this._mode = value;
        this.updateDisplay(value);
    }
    
    public get onstatechanged(): ((this: WebPlayerElement, ev: CustomEvent<{ newState: PlayerState, previousState: PlayerState; }>) => any) | null {
        return this.player.onstatechanged;
    }

    public set onstatechanged(value: ((this: WebPlayerElement, ev: CustomEvent<{ newState: PlayerState, previousState: PlayerState; }>) => any) | null) {
        if (value === this.player.onstatechanged)
            return
        this.player.onstatechanged = value;
    }

    public get onwarning(): ((this: WebPlayerElement, ev: CustomEvent<ErrorData>) => any) | null {
        return this.player.onwarning;
    }

    public set onwarning(value: ((this: WebPlayerElement, ev: CustomEvent<ErrorData>) => any) | null) {
        if (value === this.player.onwarning)
            return
        this.player.onwarning = value;
    }

    public get onpositionchanged(): ((this: WebPlayerElement, ev: Event) => any) | null {
        return this.player.onpositionchanged;
    }

    public set onpositionchanged(value: ((this: WebPlayerElement, ev: Event) => any) | null) {
        if (value === this.player.onpositionchanged)
            return
        this.player.onpositionchanged = value;
    }

    public get onframeready(): ((this: WebPlayerElement, ev: CustomEvent<Extract<TexImageSource, CanvasImageSource>>) => any) | null {
        return this.player.onframeready;
    }

    public set onframeready(value:((this: WebPlayerElement, ev: CustomEvent<Extract<TexImageSource, CanvasImageSource>>) => any) | null) {
        if (value === this.player.onframeready)
            return
        this.player.onframeready = value;
    }

    public get oninvalidpassthroughtoken():((this: WebPlayerElement, ev: Event) => any) | null  {
        return this.player.oninvalidpassthroughtoken;
    }

    public set oninvalidpassthroughtoken(value:((this: WebPlayerElement, ev: Event) => any) | null) {
        if (value === this.player.oninvalidpassthroughtoken)
            return
        this.player.oninvalidpassthroughtoken = value;
    }

    public get onmaskdecodingerror(): ((this: WebPlayerElement, ev: Event) => any) | null {
        return this.player.onmaskdecodingerror;
    }

    public set onmaskdecodingerror(value:((this: WebPlayerElement, ev: Event) => any) | null) {
        if (value === this.player.onmaskdecodingerror)
            return
        this.player.onmaskdecodingerror = value;
    }

    public get onstreamtypechange(): ((this: WebPlayerElement, ev: Event) => any) | null {
        return this.player.onstreamtypechange;
    }

    public set onstreamtypechange(value:((this: WebPlayerElement, ev: Event) => any) | null) {
        if (value === this.player.onstreamtypechange)
            return
        this.player.onstreamtypechange = value;
    }

    public get onaddvideotrack():((this: WebPlayerElement, ev: Event) => any) | null  {
        return this.player.onaddvideotrack;
    }

    public set onaddvideotrack(value:((this: WebPlayerElement, ev: Event) => any) | null) {
        if (value === this.player.onaddvideotrack)
            return
        this.player.onaddvideotrack = value;
    }

    public get onremovevideotrack(): ((this: WebPlayerElement, ev: Event) => any) | null {
        return this.player.onremovevideotrack;
    }

    public set onremovevideotrack(value:((this: WebPlayerElement, ev: Event) => any) | null) {
        if (value === this.player.onremovevideotrack)
            return
        this.player.onremovevideotrack = value;
    }

    public get onvideotrackchange(): ((this: WebPlayerElement, ev: Event) => any) | null {
        return this.player.onvideotrackchange;
    }

    public set onvideotrackchange(value:((this: WebPlayerElement, ev: Event) => any) | null) {
        if (value === this.player.onvideotrackchange)
            return
        this.player.onvideotrackchange = value;
    }
    
    public get onengineschange(): ((this: WebPlayerElement, ev: Event) => any) | null {
        return this.player.onengineschange;
    }

    public set onengineschange(value:((this: WebPlayerElement, ev: Event) => any) | null) {
        if (value === this.player.onengineschange)
            return
        this.player.onengineschange = value;
    }

    public get onenterpictureinpicture(): ((this: HTMLVideoElement, ev: Event) => any) | null {
        return this.player.onenterpictureinpicture;
    }

    public set onenterpictureinpicture(value: ((this: HTMLVideoElement, ev: Event) => any) | null) {
        if (value === this.player.onenterpictureinpicture)
            return
        this.player.onenterpictureinpicture = value;
    }

    public get onleavepictureinpicture(): ((this: HTMLVideoElement, ev: Event) => any) | null {
        return this.player.onleavepictureinpicture;
    }

    public set onleavepictureinpicture(value: ((this: HTMLVideoElement, ev: Event) => any) | null) {
        if (value === this.player.onleavepictureinpicture)
            return
        this.player.onleavepictureinpicture = value;
    }

    public get onencrypted(): ((this: HTMLMediaElement, ev: MediaEncryptedEvent) => any) | null {
        return this.player.onencrypted;
    }

    public set onencrypted(value: ((this: HTMLMediaElement, ev: MediaEncryptedEvent) => any) | null) {
        if (value === this.player.onencrypted)
            return
        this.player.onencrypted = value;
    }

    public get onwaitingforkey(): ((this: HTMLMediaElement, ev: Event) => any) | null {
        return this.player.onwaitingforkey;
    }

    public set onwaitingforkey(value: ((this: HTMLMediaElement, ev: Event) => any) | null) {
        if (value === this.player.onwaitingforkey)
            return
        this.player.onwaitingforkey = value;
    }

    //#endregion

    //#region Constructor

    constructor()
    {
        super();
        this.attachShadow({ mode: "open" });

        AllInOnePlayer.Register();

        this.shadowRoot?.appendChild(AllInOnePlayer.template.content.cloneNode(true));
        
        if(customElements.get("web-player") !== undefined)
            this.upgrade()
        else
            customElements.whenDefined("web-player").then(() => this.upgrade());
        this._mutationObserver = new MutationObserver((m) => {
            m.forEach(r => {
                if(r.type === "attributes" && r.target === this.player)
                {
                    if(r.attributeName !== null && this.observedAttributes.includes(r.attributeName.toLowerCase() as Lowercase<string>))
                    {
                        if(r.attributeName === "src")
                        {
                            const value = this.player.getAttribute(r.attributeName);
                            if(value && value.startsWith("mask://"))
                            {
                                const parts = value.substring(7).split('+');
                                this.setAttribute("src", decodeURIComponent(parts[0]));
                                this.setAttribute("masksrc", decodeURIComponent(parts[1]));
                            }
                            else
                                this.updateAttribute(r.attributeName);
                            return
                        }
                        else if(r.attributeName === "includemask" && this.hardcodedMask)
                        {
                            const value = this.player.getAttribute(r.attributeName) ?? false;
                            const ownValue = this.includeMask;
                            if(ownValue !== value)
                                return;
                        }
                        this.updateAttribute(r.attributeName);
                    }
                }
            })
        });
        this._mutationObserver.observe(this.player, {attributes: true});
        this.canvas.addEventListener(
        		'wheel',
        		(event: WheelEvent) => {
                    const delta = Math.sign(event.deltaY) * this.zoomStep;
                    if(this.currentZoom + delta <= this.zoomMax && this.currentZoom + delta >= this.zoomMin)
                    {
                        this.scene.zoom(delta / this.zoomSensitivity);
                        this.currentZoom += delta;
                    }
        			event.preventDefault()
        		},
        		false
        	);
    }

    protected connectedCallback(): void
    {
        LogInternal.info(`[${this.tagName}] Custom element added to page.`);
        this.upgradeProperty("mode");
        this.upgradeProperty("src");
        this.upgradeProperty("maskSrc");
        this.upgradeProperty("autoplay");
        this.upgradeProperty("controls");
        this.upgradeProperty("lowLatency");
        this.upgradeProperty("muted");
        this.upgradeProperty("volume");
        this.upgradeProperty("loop");
        this.upgradeProperty("width");
        this.upgradeProperty("height");
        this.upgradeProperty("disablePictureInPicture");
        this.upgradeProperty("passthroughToken");
        this.upgradeProperty("includeMask");
        this.upgradeProperty("enginesList");
        this.upgradeProperty("hardcodedMask");
        this.upgradeProperty("connectionTimeout");
        this.upgradeProperty("poster");
        this.upgradeProperty("crossOrigin");
        this.upgradeProperty("externalRenderer");
        this.upgradeProperty("stallWindow");
        this.upgradeProperty("maxStallEvents");
        this.upgradeProperty("fmp4ProtocolVersion");
        this.upgradeProperty("zoomMax");
        this.upgradeProperty("zoomMin");
        this.upgradeProperty("zoomSensitivity");
        this.upgradeProperty("zoomStep");
        this.upgradeProperty("touchZoomMode");
        this.upgradeProperty("currentZoom");
        this.upgradeProperty("requireReleaseAfterZoom");
        this.upgradeProperty("sceneColor");
        this.upgradeProperty("mouseMoveXSensitivity");
        this.upgradeProperty("mouseMoveYSensitivity");
        this.upgradeProperty("touchMoveXSensitivity");
        this.upgradeProperty("touchMoveYSensitivity");
        this.upgradeProperty("rotationXLimit");
        this.upgradeProperty("rotationYLimit");
        this.upgradeProperty("projection");
        this.upgradeProperty("onstatechanged");
        this.upgradeProperty("onwarning");
        this.upgradeProperty("onpositionchanged");
        this.upgradeProperty("onerror");
        this.upgradeProperty("onframeready");
        this.upgradeProperty("oninvalidpassthroughtoken");
        this.upgradeProperty("onmaskdecodingerror");
        this.upgradeProperty("ondurationchange");
        this.upgradeProperty("onstreamtypechange");
        this.upgradeProperty("onended");
        this.upgradeProperty("onaddvideotrack");
        this.upgradeProperty("onremovevideotrack");
        this.upgradeProperty("onvideotrackchange");
        this.upgradeProperty("onenterpictureinpicture");
        this.upgradeProperty("onleavepictureinpicture");
        this.upgradeProperty("onengineschange");

        this.projectionMesh.SetTexture(this.player);
        this.player.addEventListener("playing", () => this.projectionMesh.SetTexture(this.player));
    }

    protected disconnectedCallback(): void
    {
        LogInternal.info(`[${this.tagName}] Custom element removed from page.`);
    }

    protected adoptedCallback(): void
    {
        LogInternal.info(`[${this.tagName}] Custom element moved to new page.`);
    }


    //#endregion

    //#region Public Methods

    public cancelVideoFrameCallback(handle: number): void {
        this.player.cancelVideoFrameCallback(handle);
    }

    public getVideoPlaybackQuality(): VideoPlaybackQuality {
        return this.player.getVideoPlaybackQuality();
    }

    public async requestPictureInPicture(): Promise<PictureInPictureWindow> {
        return await this.player.requestPictureInPicture();
    }

    public requestVideoFrameCallback(callback: VideoFrameRequestCallback): number {
        return this.player.requestVideoFrameCallback(callback);
    }

    public addTextTrack(kind: TextTrackKind, label?: string, language?: string): TextTrack {
        return this.player.addTextTrack(kind, label, language);
    }

    public canPlayType(type: string): CanPlayTypeResult {
        return this.player.canPlayType(type)
    }

    public async fastSeek(time: number): Promise<void> {
        await this.player.fastSeek(time)
    }

    public async load(): Promise<void> {
        await this.player.load();
    }

    public async pause(): Promise<void> {
        await this.player.pause()
    }

    public async play(): Promise<void> {
        await this.player.play();
        this.updateProjectionMesh();
    }

    public async stop()
    {
        await this.player.stop();
    }

    public async togglePause()
    {
        await this.player.togglePause();
    }

    public async seek(time: number)
    {
        await this.player.seek(time);
    }

    public exitPictureInPicture(): void
    {
        this.player.exitPictureInPicture();
    }

    public async setSinkId(sinkId: string): Promise<void>
    {
        await this.player.setSinkId(sinkId);
    }

    public render(): void {
        this.player.render();
    }

    public async setMediaKeys(mediaKeys: MediaKeys | null): Promise<void> {
        this.player.setMediaKeys(mediaKeys);
    }

    public captureStream(): MediaStream {
        return this.player.captureStream();
    }

    //#endregion

    //#region Private Methods

    private updateSrc(src: string, maskSrc: string) {
        if(!maskSrc)
            this.player.src = src;
        else
            this.player.src = `mask://${encodeURIComponent(src)}+${encodeURIComponent(maskSrc)}`;
    }

    private updateProjectionMesh() {
        this.projectionMesh.Projection = Projection.FromString(!!this.projection ? this.projection : this.src);
        this.updateDisplay(this.mode);
    }

    private updateMask() {
        this.player.includeMask = this.includeMask && !this.hardcodedMask;
        this.projectionMesh.MaskDeo = this.hardcodedMask && this.includeMask;
    }

    private updateDisplay(mode: PlayerMode) {
        switch (mode){
            case "2d": {
                this.canvas.hidden = true;
                break;
            }
            case "3d": {
                this.canvas.hidden = false;
                break;
            }
            case "auto":{
                this.canvas.hidden = this.projectionMesh.Projection.MeshType === MeshType.Flat;
                break;
            }
        }
        this.player.hidden = !this.canvas.hidden;
    }

    private updateAttribute(name: string)
    {
        const ownAttr = this.getAttribute(name);
        const childAttr = this.player.getAttribute(name);
        if(ownAttr !== childAttr)
        {
            if (childAttr !== null)
                this.setAttribute(name, childAttr);
            else
                this.removeAttribute(name);
        }
    }

    //#endregion

    //#region Helper Functions

    private setAttributeValue<K extends string & keyof AllInOnePlayer, L extends AttributedPropertyName<AllInOnePlayer, K>, V extends (string | number | boolean | DOMTokenListLike | string[] | null) & this[L]>(name: L, value: V)
    {
        const lowercase = name.toLowerCase();
        if (!this.observedAttributes.includes(lowercase as Lowercase<string>))
            return;
        if (value === null && this.getAttribute(lowercase) !== null)
            this.removeAttribute(lowercase);
        else if (typeof value === "object" && value instanceof DOMTokenListLike)
            this.setAttribute(lowercase, value.value);
        else if (typeof value === "object" && Array.isArray(value))
        {
            const join = value.length > 0 ? value.join(' ') : "";
            if (this.getAttribute(lowercase) !== join)
                this.setAttribute(lowercase, join);
        }
        else if (typeof value === "string" && this.getAttribute(lowercase) !== value)
        {
            if(value !== "")
                this.setAttribute(lowercase, value);
            else
                this.removeAttribute(lowercase);
        }
        else if (typeof value === "number" && this.getAttribute(lowercase) !== value.toString())
            this.setAttribute(lowercase, value.toString());
        else if (typeof value === "boolean" && value && this.getAttribute(lowercase) === null)
            this.setAttribute(lowercase, "");
        else if (typeof value === "boolean" && !value && this.getAttribute(lowercase) !== null)
            this.removeAttribute(lowercase);
    }

    private static setPropertyValue<E extends AllInOnePlayer, K extends string & keyof E, L extends AttributedPropertyName<E,K>, V extends (string | boolean | number | string[] | null) & E[L]>(obj: E, name: L)
    {
        const value = obj.getAttribute(name);
        if (value === null)
            return;
        const currentValue = obj[name];
        if (typeof currentValue === "string")
            obj[name] = value as V;
        else if (typeof currentValue === "number")
            obj[name] = Number.parseFloat(value as string) as V;
        else if (typeof currentValue === "boolean")
            obj[name] = (value !== null) as V;
        else if(typeof currentValue === "object" && currentValue instanceof DOMTokenListLike)
            currentValue.setValue(value);
        else if (typeof currentValue === "object" && Array.isArray(currentValue))
            obj[name] = (value.includes(' ') ? value.split(' ') : [value]) as V;
    }

    private static upgradeProperty<E extends AllInOnePlayer, T extends string & keyof E, L extends AttributedPropertyName<E,T>>(obj: E, prop: L)
    {
        AllInOnePlayer.propertyPairs.push({property: prop, attribute: prop.toLowerCase()});
        if (obj.hasOwnProperty(prop))
        {
            const value = obj[prop];
            delete obj[prop];
            obj[prop] = value;
        }
        else if (prop in obj)
        {
            AllInOnePlayer.setPropertyValue(obj,prop);
        }
    }

    private upgradeProperty<T extends keyof AllInOnePlayer, L extends AttributedPropertyName<AllInOnePlayer,T>>(prop: L)
    {
        AllInOnePlayer.upgradeProperty(this, prop);
    }

    protected attributeChangedCallback<K extends string & keyof T & keyof AllInOnePlayer, V extends T[K], T extends ThisType<AllInOnePlayer> & { [P in K]: T[P] } = this>(name: K, oldValue: V, newValue: V)
    {
        const propName = AllInOnePlayer.propertyPairs.find(p => p.attribute === name)?.property as K ?? "";
        const propertyValue = JSON.stringify(this[propName]);
        if (propName in this)
        {
            if (typeof this[propName] === typeof newValue)
            {
                this[propName] = newValue; 
            }
            else if (typeof this[propName] === "number" && typeof newValue === "string")
            {
                this[propName] = Number.parseFloat(newValue) as V;
            }
            else if (typeof this[propName] === "number" && newValue === null)
            {
                this[propName] = 0 as V;
            }
            else if (typeof this[propName] === "boolean" && newValue === null)
            {
                this[propName] = false as V;
            }
            else if (typeof this[propName] === "string" && newValue === null)
            {
                this[propName] = "" as V;
            }
            else if (typeof this[propName] === "boolean" && typeof newValue === "string")
            {
                this[propName] = true as V;
            }
            else if (typeof this[propName] === "object" && Array.isArray(this[propName]) && typeof newValue === "string")
            {
                this[propName] = (newValue as string).split(' ') as V;
            }
            else if (typeof this[propName] === "object" && Array.isArray(this[propName]) && newValue === null)
            {
                this[propName] = [] as V;
            }
            else if (typeof this[propName] === "object" && this[propName] instanceof DOMTokenListLike && typeof newValue === "string")
            {
                this[propName].setValue(newValue as V);
            }
            else if (typeof this[propName] === "object" && this[propName] instanceof DOMTokenListLike && typeof newValue === null)
            {
                this[propName].clear();
            }
        }
        LogInternal.debug(`[${this.tagName}] Attribute '${name}' (property: '${propName}') changed, old value: ${oldValue}, new value: ${newValue}, old property value: ${propertyValue}, new property value: ${JSON.stringify(this[propName])}`);
    }

    private upgrade()
    {
        if(customElements && customElements.upgrade)
            customElements.upgrade(this.player)
    }

    //#endregion
}


//#region Registration

AllInOnePlayer.Register();

declare global
{
    interface HTMLElementTagNameMap
    {
        "all-in-one-player": AllInOnePlayer;
    }
}
//#endregion